import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import LabelData from '../../../../Util/LabelData/LabelData';
import moment from 'moment';
import './HolidayEventDetail.scss';
import ConfirmDelete from '../../../../Util/Modals/ConfirmDelete/ConfirmDelete';
import CustomDatePicker from '../../../../Util/CustomDatePicker/CustomDatePicker';
import { deleteHoliday, updateHoliday } from '../../../../../api/contentManager';
import { APPLIED_MODIFY_EVENT, CREATE_HOLIDAY_KEY, DC_COUNTRY_ID, EDIT_HOLIDAY_KEY, HOLIDAY_DATE_FORMAT, HOLIDAY_DETAILS_KEY } from './HolidayEventConstants';
import RequiredErrorMessage from '../../../../Util/Errors/RequiredErrorMessage/RequiredErrorMessage';

type HolidayEvent = {
  id?: number;
  name: string;
  description: string;
  dateHoliday: string;
  countryId: number;
}

type Props = {
  eventData?: any;
  setHideSeeMoreButton?: Function;
  onSave?: (data: HolidayEvent) => void;
  closeModal?: () => void;
  isCreateMode?: boolean;
};

export const HolidayEventDetail = (props: Props) => {
  const { eventData, setHideSeeMoreButton, onSave, closeModal, isCreateMode } = props;
  
  const [isEditing, setIsEditing] = useState(isCreateMode || false);
  const [isFirstRender, setIsFirstRender] = useState(true);
  const [form, setForm] = useState({
    name: eventData?.name || '',
    description: eventData?.description || '',
    dateHoliday: eventData?.dateHoliday ? moment(eventData.dateHoliday).format('YYYY-MM-DDT00:00:00Z') : '',
    id: eventData?.id || null,
    countryId: DC_COUNTRY_ID,
  });
  const [errors, setErrors] = useState({
    name: false,
    description: false,
    dateHoliday: false,
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const { t } = useTranslation();

  if (setHideSeeMoreButton) setHideSeeMoreButton(true);

  useEffect(() => {
    if (!isFirstRender) {
      validateForm();
    }
  }, [form]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleEdit = () => setIsEditing(true);

  const handleCancel = () => {
    setIsEditing(false);
    setForm({
      name: eventData?.name || '',
      description: eventData?.description || '',
      dateHoliday: eventData?.dateHoliday ? moment(eventData.dateHoliday).format(HOLIDAY_DATE_FORMAT) : '',
      id: eventData?.id || null,
      countryId: DC_COUNTRY_ID,
    });
    if (closeModal) closeModal();
  };

  const renderTitle = isCreateMode ? CREATE_HOLIDAY_KEY
    : isEditing ? EDIT_HOLIDAY_KEY : HOLIDAY_DETAILS_KEY;

  const validateForm = () => {
    const newErrors = {
      name: !form.name.trim(),
      description: !form.description.trim(),
      dateHoliday: !form.dateHoliday,
    };
    setErrors(newErrors);
    return !newErrors.name && !newErrors.description && !newErrors.dateHoliday;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsFirstRender(false);
    if (!validateForm()) return;
    if (onSave) {
      onSave(form);
      return;
    }

    if (!isCreateMode && isEditing) {
      updateHoliday(form)
        .finally(() => {
          window.dispatchEvent(new CustomEvent(APPLIED_MODIFY_EVENT));
        });
    }

    setIsEditing(false);
  };

  const handleDelete = (holidayId) => {
    deleteHoliday(holidayId).finally(() => {
      window.dispatchEvent(new CustomEvent(APPLIED_MODIFY_EVENT));
      if (closeModal) closeModal();
    });
  }

  return (
    <>
      <section className='holiday-detail'>
        <h3>{t(renderTitle)}</h3>
        <br />
        {isEditing ? (
          <form className="holiday-form" onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="name">{t('name')}</label>
              <input
                id="name"
                name="name"
                value={form.name}
                onChange={handleChange}
                required
                className={`form-control${errors.name ? ' input-error' : ''}`}
              />
              {errors.name && <RequiredErrorMessage />}
            </div>
            <div className="form-group">
              <label htmlFor="description">{t('description')}</label>
              <textarea
                id="description"
                name="description"
                value={form.description}
                onChange={handleChange}
                required
                className={`form-control${errors.description ? ' input-error' : ''}`}
              />
              {errors.description && <RequiredErrorMessage />}
            </div>
            <div className="form-group">
              <label htmlFor="dateHoliday">{t('date')}</label>
              <CustomDatePicker
                date={form.dateHoliday ? (() => {
                  const [year, month, day] = form.dateHoliday.split('T')[0].split('-').map(Number);
                  return new Date(year, month - 1, day);
                })() : null}
                label={''}
                setDate={(date) => {
                  const year = date.getFullYear();
                  const month = String(date.getMonth() + 1).padStart(2, '0');
                  const day = String(date.getDate()).padStart(2, '0');
                  const isoDate = `${year}-${month}-${day}T00:00:00Z`;
                  setForm((prev) => ({ ...prev, dateHoliday: isoDate }));
                  setErrors((prev) => ({ ...prev, dateHoliday: false }));
                }}
                placeholderComplete={true}
                minDate={new Date(2020, 0, 1)}
                error={errors.dateHoliday}
                style={{container: {width: 400, height: 42, boxSizing: 'border-box', justifyContent: 'flex-start'}}}
              />
              {errors.dateHoliday && <RequiredErrorMessage />}
            </div>
          </form>
        ) : (
          <>
            <LabelData label={t('name')} data={form?.name} reverse={true} />
            <br />
            <LabelData label={t('description')} data={form?.description} reverse={true} />
            <br />
            <LabelData label={t('date')} data={form?.dateHoliday ? moment(form.dateHoliday).format('DD/MM/YYYY') : ''} reverse={true} />
          </>
        )}
      </section>
      <section className="footer-holiday-detail">
        {isEditing ? (
          <>
            <button
              className="btn btn-primary"
              type="submit"
              form=""
              onClick={handleSubmit}
            >
              {t('save')}
            </button>
            <button className="btn btn-primary-outlined" type="button" onClick={handleCancel}>
              {t('cancel')}
            </button>
          </>
        ) : (
          <>
            <button className="btn btn-primary" type="button" onClick={handleEdit}>
              {t('edit')}
            </button>
            <button className="btn btn-danger" type="button" onClick={() => setShowDeleteModal(true)}>
              {t('delete')}
            </button>
            <button className="btn btn-primary-outlined" type="button" onClick={closeModal}>
              {t('close')}
            </button>
          </>
        )}
      </section>
      <ConfirmDelete
        isOpen={showDeleteModal}
        submit={() => {
          setShowDeleteModal(false);
          if (eventData?.id) handleDelete(eventData.id);
        }}
        closeModal={() => setShowDeleteModal(false)}
        customTitle={t('delete_holiday')}
        customMessage={t('delete_holiday_description')}
        submitButtonText={t('delete')}
      />
    </>
  );
};
